import { ref, Ref, onBeforeUnmount } from "vue";
import { Editor } from "@tiptap/vue-3";
import { fontFamilies } from "@/data/expert-program/font-families";

export function useStylingSettings(
  editor: Ref<Editor | undefined>,
  emit: (event: string, ...args: any[]) => void,
  initialValues: {
    fontFamily?: string;
    fontSize?: string;
    boldness?: string;
    buttonTextColor?: string;
    buttonBackgroundColor?: string;
    headerBackgroundColor?: string;
    headerTextColor?: string;
  } = {}
) {
  const fonts = fontFamilies;

  const fontSizes = ["12", "14", "16", "18"];

  const fontWeights = [
    { value: "400", label: "Regular" },
    { value: "700", label: "Bold" },
  ];

  const selectedFontFamily = ref(initialValues.fontFamily || "");
  const selectedFontSize = ref(initialValues.fontSize || "");
  const textColor = ref(initialValues.buttonTextColor || "");
  const buttonBackgroundColor = ref(initialValues.buttonBackgroundColor || "");
  const headerBackgroundColor = ref(initialValues.headerBackgroundColor || "");
  const headerTextColor = ref(initialValues.headerTextColor || "");
  const selectedBoldness = ref(initialValues.boldness || "");

  const applyFontFamily = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    const font = target.value;
    selectedFontFamily.value = font;
    emit("fontFamilyChanged", font);
    if (font) {
      editor.value
        ?.chain()
        .focus()
        .setMark("fontFamily", { fontFamily: font })
        .run();
    }
  };

  const applyFontSize = (event: Event, fontType?: string) => {
    const target = event.target as HTMLSelectElement;
    const size = target.value;
    selectedFontSize.value = size;

    // Emit generic event for backward compatibility
    emit("fontSizeChanged", size);

    // Emit specific event based on fontType if provided
    if (fontType === "header") {
      emit("headerFontSizeChanged", size);
    } else if (fontType === "paragraph") {
      emit("paragraphFontSizeChanged", size);
    }

    if (size) {
      editor.value
        ?.chain()
        .focus()
        .setMark("fontSize", { fontSize: size })
        .run();
    }
  };

  const applyBoldness = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    const boldness = target.value;
    selectedBoldness.value = boldness;
    emit("boldnessChanged", boldness);
    if (boldness) {
      editor.value?.chain().focus().setMark("boldness", { boldness }).run();
    }
  };

  const applyTextColor = (event: Event) => {
    const target = event.target as HTMLInputElement;
    textColor.value = target.value;
    emit("buttonTextColorChanged", textColor.value);
    emit("textColorChanged", textColor.value);
    editor.value
      ?.chain()
      .focus()
      .setMark("textColor", { color: textColor.value })
      .run();
  };

  const applyHeaderBackgroundColor = (event: Event) => {
    const target = event.target as HTMLInputElement;
    headerBackgroundColor.value = target.value;
    emit("headerBackgroundColorChanged", headerBackgroundColor.value);
    editor.value
      ?.chain()
      .focus()
      .setMark("headerBackgroundColor", {
        backgroundColor: headerBackgroundColor.value,
      })
      .run();
  };

  const applyButtonBackgroundColor = (event: Event) => {
    const target = event.target as HTMLInputElement;
    buttonBackgroundColor.value = target.value;
    emit("buttonBackgroundColorChanged", buttonBackgroundColor.value);
    editor.value
      ?.chain()
      .focus()
      .setMark("buttonBackgroundColor", {
        backgroundColor: buttonBackgroundColor.value,
      })
      .run();
  };

  const applyHeaderTextColor = (event: Event) => {
    const target = event.target as HTMLInputElement;
    headerTextColor.value = target.value;
    emit("headerTextColorChanged", headerTextColor.value);
    editor.value
      ?.chain()
      .focus()
      .setMark("headerTextColor", { color: headerTextColor.value })
      .run();
  };

  const alertUser = (warningState: Ref<boolean>) => {
    warningState.value = true;

    setTimeout(() => {
      warningState.value = false;
    }, 1000);
  };

  const beforeEnter = (el: Element) => {
    const target = el as HTMLElement;
    target.style.height = "0";
    target.style.opacity = "0";
  };

  const enter = (el: Element) => {
    const target = el as HTMLElement;
    const height = target.scrollHeight;
    target.style.transition = "height 0.4s ease, opacity 0.4s ease";
    target.style.height = height + "px";
    target.style.opacity = "1";
  };

  const leave = (el: Element) => {
    const target = el as HTMLElement;
    target.style.transition = "height 0.4s ease, opacity 0.4s ease";
    target.style.height = "0";
    target.style.opacity = "0";
  };

  onBeforeUnmount(() => {
    editor.value?.destroy();
  });

  return {
    fonts,
    fontSizes,
    fontWeights,
    selectedFontFamily,
    selectedFontSize,
    textColor,
    buttonBackgroundColor,
    headerBackgroundColor,
    headerTextColor,
    selectedBoldness,
    applyFontFamily,
    applyFontSize,
    applyBoldness,
    applyTextColor,
    applyHeaderBackgroundColor,
    applyButtonBackgroundColor,
    applyHeaderTextColor,
    alertUser,
    beforeEnter,
    enter,
    leave,
  };
}
